import { Fragment, type FC } from 'react'

import { Box, Divider, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { F0TagDot, type NewColor } from '@factorialco/f0-react'
import { Money } from '@factorialco/f0-react/icons/app'

import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import { getStatusConfig } from 'modules/salary_advance/lib/requestHelpers'

import type { RequestNode } from './types'

interface RequestsListProps {
  requests: ReadonlyArray<RequestNode>
  loading: boolean
  error: boolean
  locale: string
}

const statusVariantToColor: Record<string, NewColor> = {
  positive: 'viridian',
  info: 'malibu',
  warning: 'yellow',
  critical: 'barbie',
  neutral: 'smoke',
}

export const RequestsList: FC<RequestsListProps> = ({ requests, loading, error, locale }) => {
  if (loading && requests.length === 0) {
    return <RequestsLoadingState />
  }

  if (error && requests.length === 0) {
    return (
      <Box
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="s12"
        paddingY="s32"
      >
        <Text size="200" color="grey700">
          {i18n.t('errors.generic_error')}
        </Text>
      </Box>
    )
  }

  if (requests.length === 0) {
    return (
      <Box
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="s12"
        paddingY="s32"
      >
        <Box
          alignItems="center"
          justifyContent="center"
          width="s40"
          height="s40"
          borderRadius={{ all: 'abs012' }}
          background="grey100"
        >
          <Money width={18} height={18} />
        </Box>
        <Text size="200" color="grey700">
          {i18n.t('salary_advance.employee.breakdown_side_panel.requests_empty')}
        </Text>
      </Box>
    )
  }

  const completedTotalsByCurrency = requests
    .filter((request) => request.status === 'completed')
    .reduce<Map<string, number>>((totals, request) => {
      totals.set(request.currency, (totals.get(request.currency) ?? 0) + request.amountCents)
      return totals
    }, new Map())
  const completedTotals = Array.from(completedTotalsByCurrency.entries())
  const hasMultipleCompletedCurrencies = completedTotals.length > 1
  const totalAdvancedLabel = (currency: string) =>
    hasMultipleCompletedCurrencies
      ? `${i18n.t('salary_advance.employee.breakdown_side_panel.requests_total_advanced')} (${currency})`
      : i18n.t('salary_advance.employee.breakdown_side_panel.requests_total_advanced')

  return (
    <Box flexDirection="column" gap="s12">
      <Box className="overflow-hidden rounded-lg border border-solid border-f1-border-secondary">
        {requests.map((request, index) => {
          const { label, variant } = getStatusConfig(
            request.status,
            request.paymentRequestItem?.status,
            'hr_admin'
          )
          const dotColor = statusVariantToColor[variant] ?? 'smoke'

          return (
            <Fragment key={request.id}>
              <Box
                flexDirection="row"
                justifyContent="spaceBetween"
                alignItems="center"
                padding="s16"
                gap="s12"
              >
                <Box flexDirection="row" alignItems="center" gap="s8">
                  <F0TagDot text={label} color={dotColor} />
                  <Text size="200" color="grey700">
                    {convertDate(request.createdAt, 'backend_datetime', 'ui_date_with_time')}
                  </Text>
                </Box>

                <Text size="300" weight="semibold" color="grey1000">
                  {MoneyWithCurrency({
                    amount: request.amountCents,
                    currency: request.currency,
                    locale,
                  })}
                </Text>
              </Box>
              {index < requests.length - 1 && <Divider />}
            </Fragment>
          )
        })}

        {completedTotals.length > 0 && (
          <>
            <Divider />

            {completedTotals.map(([currency, totalAdvancedCents], index) => (
              <Fragment key={currency}>
                <Box
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="center"
                  padding="s16"
                >
                  <Text size="300" weight="semibold" color="grey700">
                    {totalAdvancedLabel(currency)}
                  </Text>
                  <Text size="300" weight="semibold" color="grey1000">
                    {MoneyWithCurrency({ amount: totalAdvancedCents, currency, locale })}
                  </Text>
                </Box>

                {index < completedTotals.length - 1 && <Divider />}
              </Fragment>
            ))}
          </>
        )}
      </Box>
    </Box>
  )
}

const SKELETON_ITEMS_COUNT = 2

const RequestsLoadingState: FC = () => (
  <Box flexDirection="column" gap="s12">
    <Box className="overflow-hidden rounded-2xl border border-solid border-f1-border-secondary">
      {Array.from({ length: SKELETON_ITEMS_COUNT }).map((_, index) => (
        <Fragment key={index}>
          <Box
            flexDirection="row"
            justifyContent="spaceBetween"
            alignItems="center"
            padding="s16"
            gap="s12"
          >
            <Box flexDirection="row" alignItems="center" gap="s8">
              <Skeleton width="s80" height="s20" isLoading />
              <Skeleton width="s96" height="s12" isLoading />
            </Box>
            <Skeleton width="s64" height="s16" isLoading />
          </Box>
          {index < SKELETON_ITEMS_COUNT - 1 && <Divider />}
        </Fragment>
      ))}

      <Divider />

      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" padding="s16">
        <Skeleton width="s96" height="s16" isLoading />
        <Skeleton width="s64" height="s16" isLoading />
      </Box>
    </Box>
  </Box>
)
